import type { GameState, LevelDef, Passenger, SpawnDef } from '@full-elevator/sim';
import { SERVICE } from '@full-elevator/sim';

/**
 * Meeting someone new (ADR-0036).
 *
 * Every character is introduced the moment they first appear in play: the
 * game stops, a card shows them as the game draws them, says what they do and
 * what to do about it, and once it is read a ring points them out on their
 * floor. Once per save. This is how the emergencies were already introduced
 * (ADR-0029); the owner asked for every character to be, after newcomers lost
 * the thread a few shifts in (PLAYTEST_LOG, 2026-10-16).
 *
 * It only reads the state, to find who has just appeared. It decides nothing
 * about what anyone may do.
 */

export interface Introduction {
  /** The card's heading. */
  readonly heading: string;
  /** Who they are, in two or three words. */
  readonly title: string;
  /**
   * Why they travel, in a few plain words: a person with somewhere to be, not
   * only a rule (identity review, 2026-10-27). Shared by every building.
   */
  readonly why?: string;
  /** What they do. */
  readonly what: string;
  /** What the player should do about it. */
  readonly todo: string;
  /** Who else is met with them and needs no card of their own: a call-out's return. */
  readonly with?: readonly string[];
}

const SOMEONE_NEW = 'Someone new';

/**
 * What a gold ring is worth, in points, from the sim's own numbers: "worth
 * double, and their value drains twice as fast" was an idiom with broken
 * grammar, and "value" a fourth name for points (writer review, 2026-10-26).
 */
const GOLD_RING =
  `worth ${SERVICE.deliveryBase * SERVICE.vipMultiplier} points, not ${SERVICE.deliveryBase}. ` +
  `Each second of waiting costs ${SERVICE.waitPenaltyPerSecond * SERVICE.vipMultiplier}, not ${SERVICE.waitPenaltyPerSecond}.`;

/** Every character there is, by the key `characterOf` gives them. */
export const INTRODUCTIONS: Readonly<Record<string, Introduction>> = {
  impatient: {
    heading: SOMEONE_NEW,
    title: 'In a hurry',
    why: 'Late for something.',
    what: 'The bar on their ticket is their patience. When it runs out, they leave.',
    todo: 'Get to them before it empties.',
  },
  luggage: {
    heading: SOMEONE_NEW,
    title: 'Travelling heavy',
    why: 'Off on a trip.',
    what: 'Their suitcase takes a second seat in the lift.',
    todo: 'Arrive with two seats free for them.',
  },
  courier: {
    heading: SOMEONE_NEW,
    title: 'Courier',
    why: 'A parcel is due.',
    // The whole rule: the bar keeps running aboard. Beta 1's tester C took the
    // courier on, stopped tapping, and lost (PLAYTEST_LOG, 2026-10-14).
    what: 'The bar on their ticket is their time. It keeps running in the lift. If it runs out, they leave.',
    todo: 'Take them to their floor before the bar runs out. Come with a seat free.',
  },
  vip: {
    heading: SOMEONE_NEW,
    title: 'VIP',
    why: 'Someone important.',
    what: `Gold ring: ${GOLD_RING}`,
    // That they leave, which the card never said: on the bold line, for room.
    todo: 'If their bar runs out, they leave. Serve them before the others.',
  },
  group: {
    heading: 'New faces',
    title: 'Together',
    why: 'Out together, as one party.',
    what: 'People with the same stripe get in together, or not at all.',
    todo: 'Come with a seat for every one of them.',
  },
  // Each building's own people (ADR-0031).
  postman: {
    heading: SOMEONE_NEW,
    title: 'The postman',
    // Met in the free part since ADR-0047, so he gets a reason as the five do.
    why: 'Letters for every floor.',
    what: 'He stops at every floor on his ticket before he gets off. His cart takes two seats.',
    todo: 'Open the doors at each of those floors with him aboard.',
  },
  housekeeping: {
    heading: SOMEONE_NEW,
    title: 'Housekeeping',
    // "On her floor", as the hotel's own card counts room (writer review, 2026-10-26).
    what: 'Her cart takes two places on her floor, and she never gives up waiting.',
    todo: 'Take her where she is going, and guests have room again.',
  },
  commuter: {
    heading: SOMEONE_NEW,
    title: 'Running for a train',
    what: 'The board on the platform counts down to their train.',
    todo: 'Get them down before it leaves, or they miss it.',
  },
  windowCleaner: {
    heading: SOMEONE_NEW,
    title: 'Window cleaner',
    what: 'Up to the roof with two seats of kit, a while at work, then back down.',
    todo: 'Take him up, and keep room to bring him back.',
    with: ['windowCleanerDown'],
  },
  // More of each building's own (ADR-0043).
  roomService: {
    heading: SOMEONE_NEW,
    title: 'Room service',
    what: 'The trolley takes two seats, and the bar on the ticket is the dinner going cold.',
    todo: 'Come with two seats free, before the bar runs out.',
  },
  bellhop: {
    heading: SOMEONE_NEW,
    title: 'The bellhop',
    what: 'He leaves bags at every floor on his ticket before he is done. His trolley takes three seats.',
    todo: 'Open the doors at each of those floors with him aboard.',
  },
  cyclist: {
    heading: SOMEONE_NEW,
    title: 'Cyclist',
    what: 'The bike takes two seats, and the bar on the ticket is a short patience.',
    todo: 'Come soon, with two seats free.',
  },
  ceo: {
    heading: SOMEONE_NEW,
    title: 'The chief executive',
    what: `With a bodyguard: two seats. Gold ring: ${GOLD_RING}`,
    todo: 'Serve them first, with two seats free.',
  },
  investor: {
    heading: SOMEONE_NEW,
    title: 'The investor',
    what: `Gold ring: ${GOLD_RING} The bar is their meeting.`,
    todo: 'Take them before anyone else, and before the bar runs out.',
  },
  // The hotel's departures (ADR-0051): one time for all of them, on a board.
  shuttleGuest: {
    heading: SOMEONE_NEW,
    title: 'Airport shuttle',
    why: 'A flight to catch.',
    what: 'The suitcase takes two seats. The board in the lobby counts down to the shuttle.',
    todo: 'Get them all down before it leaves, or they miss it.',
  },
  breakfastGuest: {
    heading: SOMEONE_NEW,
    title: 'Breakfast',
    why: 'Hungry.',
    what: 'Breakfast is on the top floor. The board there counts down to when it closes.',
    todo: 'Take them up before it closes, or they miss it.',
  },
  newlywed: {
    heading: 'New faces',
    title: 'The happy couple',
    why: 'Getting married today.',
    what: 'They ride together, or not at all. The board in the lobby counts down to the wedding car.',
    todo: 'Come with two seats free, before the car leaves.',
  },
  // The emergencies (ADR-0029): the crew's card says what they will come back with.
  paramedic: {
    heading: 'Ambulance!',
    title: 'Paramedics',
    what: 'Take them up to the incident. They come back down with a stretcher that takes three seats.',
    todo: 'Have room for it, and get them back to where they came in before the bar runs out — or the shift is over.',
    with: ['stretcher'],
  },
  police: {
    heading: 'Police!',
    title: 'Police',
    what: 'Take the officers up. They come back with someone under arrest, who rides alone.',
    todo: 'Bring them an empty lift, and get them back before the bar runs out — or the shift is over.',
    with: ['arrest'],
  },
};

/**
 * Which character someone is: their archetype when it has a card of its own (a
 * wedding couple travels as a party, ADR-0051), else their group, else their
 * archetype. Null for anyone ordinary.
 */
export function characterOf(passenger: Passenger): string | null {
  const own = passenger.archetype !== 'normal' && INTRODUCTIONS[passenger.archetype] !== undefined;
  const key = own
    ? passenger.archetype
    : passenger.groupId !== null
      ? 'group'
      : passenger.archetype;
  return INTRODUCTIONS[key] ? key : null;
}

/** Everyone a card introduces: the character, and whoever comes with them. */
export function introduces(key: string): string[] {
  return [key, ...(INTRODUCTIONS[key]?.with ?? [])];
}

/**
 * The next person to introduce: the first, in arrival order, who is in the
 * building as a character the player has never met. Null when there is nobody.
 */
export function nextToMeet(
  state: GameState,
  seen: readonly string[],
): { readonly passenger: Passenger; readonly key: string } | null {
  if (state.status !== 'playing') return null;
  for (const passenger of state.passengers) {
    // Waiting for the lift: on screen, and not yet anyone's concern but the player's.
    if (passenger.state !== 'waiting') continue;
    const key = characterOf(passenger);
    if (key && !seen.includes(key)) return { passenger, key };
  }
  return null;
}

/** Which character a spawn brings, or null for anyone ordinary or a call-out's return. */
export function characterOfSpawn(spawn: SpawnDef): string | null {
  if (spawn.follows !== undefined) return null;
  const archetype = spawn.archetype ?? 'normal';
  const own = archetype !== 'normal' && INTRODUCTIONS[archetype] !== undefined;
  const key = own ? archetype : spawn.group ? 'group' : archetype;
  return INTRODUCTIONS[key] ? key : null;
}

/**
 * Who each shift brings for the first time, in play order: what the home
 * screen hints at before the player gets there (ADR-0037).
 */
export function debuts(levels: readonly LevelDef[]): Map<string, string> {
  const seen = new Set<string>();
  const found = new Map<string, string>();
  for (const level of levels) {
    for (const spawn of level.spawns) {
      const key = characterOfSpawn(spawn);
      if (!key || seen.has(key)) continue;
      seen.add(key);
      if (!found.has(level.id)) found.set(level.id, key);
    }
  }
  return found;
}
